/* eslint-disable @typescript-eslint/no-explicit-any */
import { ModuleConfig } from '@schamburg-web/interfaces';
import { AdvancedMarkerRef } from '@vis.gl/react-google-maps';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useModules } from '../ModulesContext/ModulesContext';

interface MapContextProps {
  center: { lat: number; lng: number };
  zoom: number;
  setCenter: (center: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  selectedMarker: AdvancedMarkerRef | null;
  selectedIcon: { icon: any; module: ModuleConfig } | null;
  setSelectedMapIcon: (
    icon: { icon: any; module: ModuleConfig } | null,
    marker: AdvancedMarkerRef | null
  ) => void;
  tilesLoaded: boolean;
  setTilesLoaded: (loaded: boolean) => void;
  mapMessages: string[];
  addMapMessage: (message: string) => void;
  removeMapMessage: (message: string) => void;
  clearMapMessages: () => void;
}

const MapContext = createContext<MapContextProps | undefined>(undefined);

export const MapProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [center, setCenter] = useState<{ lat: number; lng: number }>({
    lat: 0,
    lng: 0,
  });
  const [tilesLoaded, setTilesLoaded] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [selectedMarker, setSelectedMarker] = useState<AdvancedMarkerRef>(null);
  const [selectedIcon, setSelectedIcon] = useState<{
    icon: any;
    module: ModuleConfig;
  } | null>(null);
  const [mapMessages, setMapMessages] = useState<string[]>([]);

  const addMapMessage = (message: string) => {
    setMapMessages((prev) => {
      if (!prev.includes(message)) {
        return [...prev, message];
      }
      return prev;
    });
  };

  const removeMapMessage = (message: string) => {
    setMapMessages((prev) => prev.filter((m) => m !== message));
  };

  const clearMapMessages = () => {
    setMapMessages([]);
  };

  const { getStore, getModuleSlice } = useModules();

  // Subscribe to Redux changes to update selected icon with fresh data
  useEffect(() => {
    if (!selectedIcon?.icon || !selectedIcon?.module?.name) {
      return; // No selected icon to update
    }

    const store = getStore();
    if (!store) return;

    const unsubscribe = store.subscribe(() => {
      // Get fresh data inline to avoid dependency issues
      const moduleSlice = getModuleSlice(selectedIcon.module.name);

      if (!moduleSlice) {
        return;
      }

      const state = store.getState();
      const displayObjects = moduleSlice.selectors.selectDisplayObjects(state);

      // Find the fresh version of the selected object
      const freshIcon = displayObjects.find(
        (obj) => obj.id === selectedIcon.icon.id
      );

      if (freshIcon && freshIcon !== selectedIcon.icon) {
        setSelectedIcon((prev) =>
          prev
            ? {
                icon: freshIcon,
                module: prev.module,
              }
            : null
        );
      }
    });

    return unsubscribe;
  }, [
    selectedIcon?.icon?.id,
    selectedIcon?.module?.name,
    getStore,
    getModuleSlice,
    selectedIcon?.icon,
  ]);

  const setSelectedMapIcon = (
    newSelectedIcon: { icon: any; module: ModuleConfig } | null,
    selectedMarker: AdvancedMarkerRef | null
  ) => {
    setSelectedMarker(selectedMarker);
    setSelectedIcon(newSelectedIcon);
  };

  return (
    <MapContext.Provider
      value={{
        center,
        zoom,
        selectedMarker,
        selectedIcon,
        setCenter,
        setZoom,
        setSelectedMapIcon,
        tilesLoaded,
        setTilesLoaded,
        mapMessages,
        addMapMessage,
        removeMapMessage,
        clearMapMessages,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};

export const useMapContext = (): MapContextProps => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within a MapProvider');
  }
  return context;
};
